<!-- src/lib/components/platform/pages/home/Header.svelte -->
<script lang="ts">
	import { Splide, SplideSlide } from '@splidejs/svelte-splide';
	import '@splidejs/splide/css';
	import Button from '$lib/components/ui/Button.svelte';
	import { page } from '$app/stores';
	import Announcement from '$lib/components/sections/Announcement.svelte';
	import Player from '@vimeo/player';
	import { onMount } from 'svelte';

	let videoPlayers: Record<string, Player> = {};
	let videoContainers: Record<string, HTMLElement> = {};
	let isCarouselReady = false;

	onMount(() => {
		// Set carousel as ready after a small delay to ensure smooth transition
		setTimeout(() => {
			isCarouselReady = true;
		}, 100);

		$page.data.headers?.forEach((header) => {
			if (header.video?.vimeo_id) {
				const iframe = document.getElementById(`video-${header.id}`);
				if (iframe) {
					const player = new Player(iframe);
					videoPlayers[header.id] = player;
					player.on('play', () => {
						if (videoContainers[header.id]) {
							videoContainers[header.id].style.opacity = '1';
						}
					});
				}
			}
		});
	});

	$: firstHeader = $page.data.headers?.[0];
</script>

{#if !isCarouselReady && firstHeader}
	<!-- Initial loading state showing first header -->
	<header
		class="relative overflow-hidden bg-black bg-cover bg-center text-white transition-opacity duration-500"
		style="background-image:url('{firstHeader.video?.public_image_url}')"
	>
		<div class="relative flex h-[100svh] w-[100%] items-center justify-center overflow-hidden">
			<div
				class="absolute left-0 top-0 flex h-[100svh] w-[100%] items-center justify-center bg-black/10"
			>
				<div
					class="items-left m-auto flex h-[100svh] w-full max-w-screen-2xl flex-col justify-center gap-32 p-8"
				>
					<div class="items-left justify-left z-10 flex max-w-[600px] flex-col gap-6 md:mt-0">
						<h1
							class="text-left text-3xl font-bold normal-case !leading-tight md:text-4xl lg:text-5xl"
						>
							{firstHeader.title}
						</h1>
						<p class="max-w-[600px] text-left text-xl">
							{firstHeader.subtitle}
						</p>
						{#if firstHeader.btn_text}
							<a href={firstHeader.btn_link}>
								<Button size="lg">{firstHeader.btn_text}</Button>
							</a>
						{/if}
					</div>
				</div>
			</div>
		</div>
	</header>
{/if}

<div
	class:opacity-0={!isCarouselReady}
	class:hidden={!isCarouselReady}
	class="transition-opacity duration-500"
>
	<Splide
		options={{
			type: 'fade',
			rewind: true,
			arrows: false, //$page.data.headers?.length > 1,
			pagination: $page.data.headers?.length > 1,
			autoplay: true,
			interval: 50000,
			paginationPosition: 'bottom',
			paginationMargin: '25vh'
		}}
		class="custom-pagination"
	>
	{#each $page.data.headers || [] as header (header.id)}
		<SplideSlide>
			<header
				class="relative overflow-hidden bg-black bg-cover bg-center text-white"
				style="background-image:url('{header.video?.public_image_url}')"
			>
				<div class="relative flex h-[100svh] w-[100%] items-center justify-center overflow-hidden">
					<div
						class="video-container"
						bind:this={videoContainers[header.id]}
						style="opacity:0; transition: all 3s;"
					>
						<iframe
							title="header-video"
							id="video-{header.id}"
							class=""
							src="https://player.vimeo.com/video/{header.video
								?.vimeo_id}?background=1&autopause=false"
							frameborder="0"
							height="100%"
							width="100%"
							allow="autoplay; fullscreen"
							allowfullscreen
						></iframe>
						<div class="overlay bg-black/30" />
					</div>
				</div>

				<div
					class="absolute left-0 top-0 flex h-[100svh] w-[100%] items-center justify-center bg-black/10"
				>
					<div
						class="items-left m-auto flex h-[100svh] w-full max-w-screen-2xl flex-col justify-center gap-32 p-8"
					>
						<div class="items-left justify-left z-10 flex max-w-[600px] flex-col gap-6 md:mt-0">
							<h1
								class="text-left text-3xl font-bold normal-case !leading-tight md:text-4xl lg:text-5xl"
							>
								{header.title}
							</h1>
							<p class="max-w-[600px] text-left text-xl">
								{header.subtitle}
							</p>
							{#if header.btn_text}
								<a href={header.btn_link}>
									<Button size="lg">{header.btn_text}</Button>
								</a>
							{/if}
						</div>
					</div>
				</div>
			</header>
		</SplideSlide>
	{/each}
	</Splide>
</div>

<Announcement />

<style>
	.video-container {
		position: absolute;
		container-type: size;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
	}

	.video-container iframe,
	.video-container .overlay {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 100%;
		height: 100%;
		pointer-events: none;

		@container (min-aspect-ratio: 16/9) {
			height: 56.25cqw;
		}

		@container (max-aspect-ratio: 16/9) {
			width: 177.78cqh;
		}
	}
	:global(.custom-pagination .splide__pagination) {
		bottom: 22vh !important; /* Quarter of viewport height */
	}

	/* Optional: If you want to keep the pagination centered horizontally */
	:global(.custom-pagination .splide__pagination.splide__pagination--ltr) {
		left: 50%;
		transform: translateX(-50%);
	}
</style>
